/**
 * SFrame126 – IAD Step 5: Locate Important Documents
 * Refactored to Checklist-based UX with category-specific instructions and Notes.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ProgressHeader from '../settlement/ProgressHeader';
import ChecklistItem from '../settlement/ChecklistItem'; 
import DocumentUploadSection from '../settlement/DocumentUploadSection';
import { useSettlementSession } from '../../hooks/useSettlementSession';
import {
  loadResidencePanelCompletion,
  withEstatePath,
} from '../../utils/secureResidenceFlow';
import { 
  PlannerDataSection 
} from '../settlement/PlannerDataSection';
import { SETTLEMENT_PLANNER_LAYOUT } from '../settlement/settlementTheme';
import { 
  listSettlementDocuments, 
  uploadSettlementDocument, 
  deleteSettlementDocument, 
  toDocumentUploadRow 
} from '../../utils/settlementDocuments';

interface FrameDataWithNotes {
  completed?: boolean;
  notes?: string;
}

const PHASE = 'iad';
const STEP_ID = 'iad-5';
const OVERVIEW_ROUTE = '/sframe-141';
const isPlanner = true;
 
const containerClassName = isPlanner ? SETTLEMENT_PLANNER_LAYOUT.container : '';
const contentColumnClassName = isPlanner ? SETTLEMENT_PLANNER_LAYOUT.contentColumn : '';
const titleClassName = isPlanner ? SETTLEMENT_PLANNER_LAYOUT.title : 'font-display text-[28px] leading-tight text-blue-900';
const subtitleClassName = isPlanner ? SETTLEMENT_PLANNER_LAYOUT.subtitle : 'text-sm text-grey-600 mb-6 leading-relaxed';

// Category-specific instructions for the upload section
const DOCUMENT_PANELS = [
  { 
    frameId: 'frame-doc-will', 
    title: 'Will and Estate Documents', 
    intro: 'Locate the will and key estate planning documents.',
    uploadInstruction: 'Upload any related legal or personal documents you find (e.g., wills, powers of attorney, personal records).'
  },
  { 
    frameId: 'frame-doc-trust', 
    title: 'Trust Documents', 
    intro: 'Find any trust agreements or amendments.',
    uploadInstruction: ' Upload trust agreements or amendments if available.'
  },
  { 
    frameId: 'frame-doc-financial', 
    title: 'Financial Records', 
    intro: 'Gather key account and financial documents.',
    uploadInstruction: 'Upload statements, tax documents, or any financial records you locate.'
  },
  { 
    frameId: 'frame-doc-insurance', 
    title: 'Insurance Policies', 
    intro: 'Locate life, health, and property insurance.',
    uploadInstruction: 'Upload insurance policies or related documents if found.'
  },
  { 
    frameId: 'frame-doc-property', 
    title: 'Property Documents', 
    intro: 'Find documents related to real estate and property.',
    uploadInstruction: 'Upload property-related documents such as deeds or mortgage records.'
  },
  { 
    frameId: 'frame-doc-digital', 
    title: 'Digital Access and Accounts', 
    intro: 'Identify important digital accounts and access points.',
    uploadInstruction: 'Upload any access-related documents or notes if helpful.'
  },
];

const CHECKLIST_GUIDANCE = [
  {
    what: 'Find the will and documents outlining final wishes.',
    why: 'Determines authority and next legal steps.',
    actions: ['Look for the original will or copies', 'Check with attorney or family members', 'Identify where the original is stored', 'Do not remove or alter documents'],
  },
  {
    what: 'Locate trust documents that may control assets.',
    why: 'Trusts may bypass probate and guide distribution.',
    actions: ['Search for trust agreements and amendments', 'Check with attorneys or advisors', 'Identify trustees named in documents', 'Note where originals are stored'],
  },
  {
    what: 'Identify accounts and financial activity.',
    why: 'Helps understand assets and obligations.',
    actions: ['Look for bank and investment statements', 'Check mail and email for account notices', 'Identify recurring bills or income', 'Make a simple list of accounts found'],
  },
  {
    what: 'Find policies that may provide coverage or benefits.',
    why: 'Insurance may provide funds or require notification.',
    actions: ['Look for life insurance policies', 'Identify home and auto insurance providers', 'Check employer or benefits records', 'Note policy details if available'],
  },
  {
    what: 'Locate deeds, mortgages, and ownership records.',
    why: 'Confirms ownership and responsibilities.',
    actions: ['Locate property deeds and mortgage documents', 'Check tax or HOA records', 'Identify property addresses', 'Note where originals are stored'],
  },
  {
    what: 'Find access to email, devices, and accounts.',
    why: 'Access is needed for communication and account management.',
    actions: ['Identify email accounts and devices', 'Look for password managers or written records', 'Check for recovery options', 'Work with family if access is unclear'],
  },
];

function renderPlanner144Summary(data: Record<string, unknown>) {
  const legal = (data.legal_documents || data.legalDocuments) as Record<string, unknown>[] | undefined;
  if (!legal?.length) return null;

  return (
    <div className="space-y-3 text-sm text-stone-800">
      <div>
        <h4 className="font-semibold text-stone-900 mb-1">Legal Documents</h4>
        {legal.slice(0, 3).map((d, i) => (
          <div key={i} className="text-stone-600 ml-2">• {typeof d.name === 'string' ? d.name : typeof d.typeLabel === 'string' ? d.typeLabel : ''}</div>
        ))}
      </div>
    </div>
  );
}

export default function SFrame126() {
  const { estateId: paramEstateId } = useParams<{ estateId: string }>();
  const estateId = paramEstateId || null;
  const navigate = useNavigate();
  const { error, loadFrameData, loadPhaseProgress, updateStepProgress, saveFrameData } = useSettlementSession(estateId);

  const [completedPanels, setCompletedPanels] = useState(0);
  const [panelDone, setPanelDone] = useState<Record<string, boolean>>({});
  const [documentsByPanel, setDocumentsByPanel] = useState<Record<string, ReturnType<typeof toDocumentUploadRow>[]>>({});
  const [notes, setNotes] = useState('');

  const refresh = useCallback(async () => {
    if (!estateId) return;
    
    // Load checklist completion
    const { byFrameId } = await loadResidencePanelCompletion(estateId, loadFrameData);
    const docFrameIds = DOCUMENT_PANELS.map(p => p.frameId);
    setCompletedPanels(Object.keys(byFrameId).filter(id => docFrameIds.includes(id) && byFrameId[id]).length);
    setPanelDone(byFrameId);

    // Load category documents
    const docMap: Record<string, ReturnType<typeof toDocumentUploadRow>[]> = {};
    for (const panel of DOCUMENT_PANELS) {
      const rows = await listSettlementDocuments(estateId, PHASE, panel.frameId);
      docMap[panel.frameId] = rows.map(toDocumentUploadRow);
    }
    setDocumentsByPanel(docMap);

    // Load step-level notes
    const frameData = await loadFrameData(STEP_ID) as FrameDataWithNotes;
    if (frameData?.notes) setNotes(frameData.notes);
  }, [estateId, loadFrameData]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleToggleComplete = async (frameId: string, newState: boolean) => {
    setPanelDone(prev => ({ ...prev, [frameId]: newState }));
    if (estateId) {
      await saveFrameData(frameId, { completed: newState });
      await refresh();
      const steps = await loadPhaseProgress(PHASE);
      if (!steps.find(s => s.stepId === STEP_ID)?.status || steps.find(s => s.stepId === STEP_ID)?.status === 'not-started') {
        await updateStepProgress(STEP_ID, PHASE, { status: 'in-progress' });
      }
    }
  };

  const handleDocUpload = async (panelId: string, files: File[]) => {
    if (!files.length) return;

    // FIX: Structure the mock object precisely to map through toDocumentUploadRow without being emptied out
    const tempDocs = files.map(file => ({
      id: `temp-${Date.now()}-${Math.random()}`,
      filename: file.name,
      mimeType: file.type,
      size: file.size,
      createdAt: new Date().toISOString()
    }));

    // Double cast through unknown allows mapping target fields cleanly without explicit-any rules firing
    const normalizedTemp = tempDocs.map(doc => toDocumentUploadRow(doc as unknown as Parameters<typeof toDocumentUploadRow>[0]));
    
    setDocumentsByPanel(prev => ({
      ...prev,
      [panelId]: [...(prev[panelId] || []), ...normalizedTemp]
    }));

    // 2. SERVER PERSISTENCE
    if (estateId) {
      try {
        await Promise.all(
          files.map(file =>
            uploadSettlementDocument({
              estateId,
              phase: PHASE,
              stepId: panelId,
              file,
            })
          )
        );

        // Fetch final database records to completely overwrite client-side optimistic UI structures safely
        const finalRows = await listSettlementDocuments(estateId, PHASE, panelId);
        setDocumentsByPanel(prev => ({
          ...prev,
          [panelId]: finalRows.map(row => toDocumentUploadRow(row))
        }));

        await updateStepProgress(STEP_ID, PHASE, { status: 'in-progress' });
      } catch (err) {
        console.error("Backend upload failed", err);
      }
    }
  };

  const handleDocDelete = async (panelId: string, docId: string) => {
    if (!confirm('Are you sure you want to remove this document?')) return;

    setDocumentsByPanel(prev => ({
      ...prev,
      [panelId]: (prev[panelId] || []).filter(d => d.id !== docId)
    }));

    if (estateId && !docId.toString().startsWith('temp-')) {
      try {
        await deleteSettlementDocument(estateId, docId);
      } catch (err) {
        console.error("Backend delete failed", err);
      }
    }
  };

  const handleNoteChange = async (val: string) => {
    setNotes(val);
    if (estateId) {
      await saveFrameData(STEP_ID, { notes: val } as Record<string, unknown>);
    }
  };

  return (
    <div className={containerClassName}>
      <div className={contentColumnClassName}>
        {error && <div className="mb-4 bg-orange-100 p-3 rounded text-orange-900 text-sm">{error}</div>}

        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate(withEstatePath(OVERVIEW_ROUTE, estateId))} className="w-[35px] h-[35px] flex items-center justify-center text-grey-600 hover:text-blue-900 transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>
          <h1 className={titleClassName}>Locate Important Documents</h1>
        </div>

        <p className={subtitleClassName}>Find the key documents you’ll need to manage the estate and take next steps.</p>

        <div className="mb-8">
          <PlannerDataSection
            estateId={estateId ?? ''}
            plannerFrameId="frame-144"
            title="Document inventory from estate plan"
            emptyMessage="No structured document inventory found."
            renderData={renderPlanner144Summary}
          />
        </div>

        <ProgressHeader
          title="Locate documents"
          variant="planner"
          subtitle="Focus on identifying what exists—you can organize everything later."
          completedSteps={completedPanels}
          totalSteps={DOCUMENT_PANELS.length}
        />

        <div className="space-y-4 mt-8">
          {DOCUMENT_PANELS.map((panel, index) => (
            <ChecklistItem
              key={panel.frameId}
              id={panel.frameId}
              title={panel.title}
              description={panel.intro}
              whatThisIs={CHECKLIST_GUIDANCE[index].what}
              whyItMatters={CHECKLIST_GUIDANCE[index].why}
              bulletPoints={CHECKLIST_GUIDANCE[index].actions}
              checked={!!panelDone[panel.frameId]}
              onChange={handleToggleComplete}
            >
              <div className="mt-6 pt-6 border-t border-stone-100">
                {/* Category-Specific Instruction Line */}
                <p className="text-sm text-stone-500 italic mb-4">
                  {panel.uploadInstruction}
                </p>
                <DocumentUploadSection
                  documents={documentsByPanel[panel.frameId] || []}
                  onUpload={(files) => handleDocUpload(panel.frameId, files)}
                  onDelete={(docId) => handleDocDelete(panel.frameId, docId)}
                  disabled={!estateId}
                  className="mb-4"
                />
              </div>
            </ChecklistItem>
          ))}
        </div>

        {/* --- Notes Section --- */}
        <div className="mt-10 pt-10 border-t border-stone-200">
          <h3 className="text-lg font-semibold text-stone-900 mb-2">Notes</h3>
          <textarea
            className="w-full rounded-lg border border-stone-300 p-4 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all min-h-[120px]"
            placeholder="Add details, locations, or reminders to keep track of your progress here"            value={notes}
            onChange={(e) => handleNoteChange(e.target.value)}
          />
        </div>
      
      </div>
    </div>
  );
}